<template>
  <div class="admin-dashboard">
    <!-- Navbar / Header -->
    <header class="admin-header py-3 px-4 d-flex justify-content-between align-items-center">
      <div class="brand">
        <h2 class="admin-title m-0">KP Portfolio Admin</h2>
      </div>
      <div class="d-flex align-items-center gap-3">
        <span class="user-badge" v-if="user.name">Signed in as: <strong>{{ user.name }}</strong></span>
        <button @click="handleLogout" class="btn btn-outline-danger btn-sm">Sign Out</button>
      </div>
    </header>

    <div class="container-fluid py-4">
      <!-- Tabs Switcher -->
      <div class="row mb-4">
        <div class="col-12">
          <ul class="nav nav-pills gap-2 admin-tabs justify-content-center">
            <li class="nav-item">
              <button 
                class="nav-link tab-btn" 
                :class="{ active: currentTab === 'ports' }"
                @click="currentTab = 'ports'"
              >
                Portfolio Items
              </button>
            </li>
            <li class="nav-item">
              <button 
                class="nav-link tab-btn" 
                :class="{ active: currentTab === 'categories' }"
                @click="currentTab = 'categories'"
              >
                Categories
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="loading" class="text-center py-5 text-white">
        <div class="spinner-border text-info" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Fetching dashboard data...</p>
      </div>

      <!-- Tab Content: Portfolio Items -->
      <div v-else-if="currentTab === 'ports'">
        <div class="row align-items-center mb-3">
          <div class="col-md-4 mb-2">
            <select class="form-select dark-select" v-model="filterCategory" @change="fetchPorts">
              <option value="">All Categories</option>
              <option v-for="cat in categories" :key="cat._id" :value="cat._id">
                {{ cat.name }}
              </option>
            </select>
          </div>
          <div class="col-md-4 mb-2">
            <select class="form-select dark-select" v-model="displayLanguage">
              <option v-for="lang in languages" :key="lang" :value="lang">
                Display: {{ lang.toUpperCase() }}
              </option>
            </select>
          </div>
          <div class="col-md-4 mb-2 text-md-end">
            <button class="btn btn-primary px-4" @click="openPortModal(null)">
              Add Portfolio Item
            </button>
          </div>
        </div>

        <div class="row" v-if="ports.length > 0">
          <div class="col-xl-4 col-md-6 col-12 mb-4" v-for="port in ports" :key="port._id">
            <div class="admin-card">
              <div class="card-img-wrapper">
                <img :src="getImage(port?.images)" @error="handleImgError" class="card-img" alt="Portfolio Image">
              </div>
              <div class="card-info p-3">
                <h5 class="fw-bold mb-2">
                  {{ port.name[displayLanguage] || port.name[languages[0]] }}
                  <span class="badge bg-secondary ms-2">{{ getCategoryName(port.category) }} ({{ port.order_show }})</span>
                </h5>
                <p class="small text-truncate-3 m-0"><strong>Link:</strong> {{ port.link[displayLanguage] }}</p>
                <p class="small text-truncate-3 m-0"><strong>Content:</strong> {{ port.content[displayLanguage] }}</p>
                <p class="small text-truncate-3 m-0"><strong>Tech:</strong> {{ port.technology[displayLanguage] }}</p>
                <p class="small text-truncate-3 m-0"><strong>API:</strong> {{ port.api[displayLanguage] }}</p>
              </div>
              <div class="card-actions p-3 d-flex justify-content-end gap-2 border-top border-secondary">
                <button class="btn btn-info btn-sm text-white" @click="openPortModal(port)">Edit</button>
                <button class="btn btn-danger btn-sm" @click="deletePort(port._id)">Delete</button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-5 text-white">
          <p>No portfolio items found. Click "Add Portfolio Item" to create one.</p>
        </div>
      </div>

      <!-- Tab Content: Categories -->
      <div v-else-if="currentTab === 'categories'">
        <div class="row justify-content-between align-items-center mb-3">
          <div class="col-12 col-md-6">
            <h4 class="text-white">Categories Management</h4>
          </div>
          <div class="col-12 col-md-4 text-md-end">
            <button class="btn btn-primary" @click="openCategoryModal(null)">
              Add Category
            </button>
          </div>
        </div>

        <div class="row justify-content-center">
          <div class="col-lg-8 col-12">
            <div class="table-responsive admin-table-container">
              <table class="table table-dark table-hover m-0">
                <thead>
                  <tr>
                    <th>Category Name</th>
                    <th>Show Order</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cat in categories" :key="cat._id">
                    <td>{{ cat.name }}</td>
                    <td>{{ cat.order_show }}</td>
                    <td class="text-end">
                      <button class="btn btn-info btn-sm text-white me-2" @click="openCategoryModal(cat)">Edit</button>
                      <button class="btn btn-danger btn-sm" @click="deleteCategory(cat._id)">Delete</button>
                    </td>
                  </tr>
                  <tr v-if="categories.length === 0">
                    <td colspan="3" class="text-center">No categories found. Click "Add Category" to create one.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Portfolio Add/Edit Modal (Vanilla CSS Backdrop & Modal) -->
    <div class="custom-modal-backdrop" v-if="showPortModal" @click.self="showPortModal = false">
      <div class="custom-modal-content">
        <div class="custom-modal-header d-flex justify-content-between align-items-center">
          <h5 class="fw-bold m-0">{{ form.id ? 'Edit' : 'Add' }} Portfolio Item</h5>
          <button class="btn-close btn-close-white" @click="showPortModal = false"></button>
        </div>
        <form @submit.prevent="savePort" class="custom-modal-body">
          <!-- Language Tabs -->
          <ul class="nav nav-tabs mb-3 dark-tabs">
            <li class="nav-item" v-for="lang in languages" :key="lang">
              <button 
                type="button" 
                class="nav-link" 
                :class="{ active: activeFormLang === lang }"
                @click="activeFormLang = lang"
              >
                {{ lang.toUpperCase() }}
              </button>
            </li>
          </ul>

          <div class="tab-content border-0 mb-3" v-for="lang in languages" :key="lang" v-show="activeFormLang === lang">
            <div class="mb-3">
              <label class="form-label">Name ({{ lang.toUpperCase() }})</label>
              <input type="text" v-model="form.name[lang]" class="form-control dark-input" :required="activeFormLang === lang" />
            </div>
            <div class="mb-3">
              <label class="form-label">Link Data ({{ lang.toUpperCase() }})</label>
              <textarea v-model="form.link[lang]" class="form-control dark-input" rows="2"></textarea>
            </div>
            <div class="mb-3">
              <label class="form-label">Content ({{ lang.toUpperCase() }})</label>
              <textarea v-model="form.content[lang]" class="form-control dark-input" rows="2"></textarea>
            </div>
            <div class="mb-3">
              <label class="form-label">Technology ({{ lang.toUpperCase() }})</label>
              <textarea v-model="form.technology[lang]" class="form-control dark-input" rows="2"></textarea>
            </div>
            <div class="mb-3">
              <label class="form-label">API ({{ lang.toUpperCase() }})</label>
              <textarea v-model="form.api[lang]" class="form-control dark-input" rows="2"></textarea>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Image URL / Link</label>
            <input type="text" v-model="form.images_link" class="form-control dark-input" />
          </div>
          <div class="mb-3">
            <label class="form-label">Image File Upload</label>
            <input type="file" ref="imagesFile" class="form-control dark-input" accept="image/*" />
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Category</label>
              <select v-model="form.category" class="form-select dark-select" required>
                <option v-for="cat in categories" :key="cat._id" :value="cat._id">
                  {{ cat.name }}
                </option>
              </select>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Order Show</label>
              <input type="text" v-model="form.order_show" class="form-control dark-input" />
            </div>
          </div>

          <div class="custom-modal-footer d-flex justify-content-end gap-2 pt-3 border-top border-secondary">
            <button type="button" class="btn btn-secondary" @click="showPortModal = false">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Changes</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Category Add/Edit Modal -->
    <div class="custom-modal-backdrop" v-if="showCategoryModal" @click.self="showCategoryModal = false">
      <div class="custom-modal-content max-w-sm">
        <div class="custom-modal-header d-flex justify-content-between align-items-center">
          <h5 class="fw-bold m-0">{{ catForm.id ? 'Edit' : 'Add' }} Category</h5>
          <button class="btn-close btn-close-white" @click="showCategoryModal = false"></button>
        </div>
        <form @submit.prevent="saveCategory" class="custom-modal-body">
          <div class="mb-3">
            <label class="form-label">Category Name</label>
            <input type="text" v-model="catForm.name" class="form-control dark-input" required />
          </div>
          <div class="mb-3">
            <label class="form-label">Order Show</label>
            <input type="text" v-model="catForm.order_show" class="form-control dark-input" />
          </div>

          <div class="custom-modal-footer d-flex justify-content-end gap-2 pt-3 border-top border-secondary">
            <button type="button" class="btn btn-secondary" @click="showCategoryModal = false">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

interface PortItem {
  _id: string
  name: Record<string, string>
  link: Record<string, string>
  content: Record<string, string>
  technology: Record<string, string>
  api: Record<string, string>
  images?: string
  category: any
  order_show: any
}

interface CategoryItem {
  _id: string
  name: string
  order_show: any
}

export default defineComponent({
  name: 'AdminPage',
  data() {
    return {
      baseUrl: process.env.VUE_APP_DATA_URL || '/api/',
      currentTab: 'ports',
      loading: true,
      languages: ['th', 'en'],
      displayLanguage: 'en',
      activeFormLang: 'en',
      user: { name: '', id: '' },
      categories: [] as CategoryItem[],
      ports: [] as PortItem[],
      filterCategory: '',
      
      // Port Modal & Form
      showPortModal: false,
      form: {
        id: '',
        name: {} as Record<string, string>,
        link: {} as Record<string, string>,
        content: {} as Record<string, string>,
        technology: {} as Record<string, string>,
        api: {} as Record<string, string>,
        images_link: '',
        category: '',
        order_show: ''
      },

      // Category Modal & Form
      showCategoryModal: false,
      catForm: {
        id: '',
        name: '',
        order_show: ''
      }
    }
  },
  mounted() {
    // Hide standard layout footer in dashboard
    const footer = document.querySelector(".thisfooter");
    if (footer) {
      footer.classList.add("d-none");
    }
    this.checkAuthAndLoad();
  },
  unmounted() {
    const footer = document.querySelector(".thisfooter");
    if (footer) {
      footer.classList.remove("d-none");
    }
  },
  methods: {
    getImage(imgUrl?: string) {
      if (!imgUrl || imgUrl.trim() === "" || imgUrl.toLowerCase() === "no image" || imgUrl.toLowerCase() === "no_image" || imgUrl.toLowerCase() === "noimage") {
        return require("@/assets/images/blank.jpg");
      }
      return imgUrl;
    },
    handleImgError(e: Event) {
      const target = e.target as HTMLImageElement;
      target.src = require("@/assets/images/blank.jpg");
    },
    checkAuthAndLoad() {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        this.$router.push('/login');
        return;
      }
      this.user = JSON.parse(storedUser);

      Promise.all([
        fetch(this.baseUrl + 'config').then(res => res.json()),
        fetch(this.baseUrl + 'category').then(res => res.json()),
        fetch(this.baseUrl + 'portfolio').then(res => res.json())
      ])
      .then(([configRes, catRes, portRes]) => {
        if (configRes.success) {
          this.languages = configRes.languages || ['th', 'en'];
          this.displayLanguage = this.languages[0] || 'en';
          this.activeFormLang = this.languages[0] || 'en';
        }
        if (catRes.success) {
          this.categories = catRes.return || [];
        }
        if (portRes.success) {
          this.ports = portRes.return || [];
        }
        this.loading = false;
      })
      .catch(err => {
        console.error("Dashboard init failed", err);
        this.$router.push('/login');
      });
    },
    fetchPorts() {
      fetch(`${this.baseUrl}portfolio?cat=${this.filterCategory}`)
        .then(res => res.json())
        .then(res => {
          if (res.success) {
            this.ports = res.return;
          }
        });
    },
    fetchCategories() {
      fetch(this.baseUrl + 'category')
        .then(res => res.json())
        .then(res => {
          if (res.success) {
            this.categories = res.return;
          }
        });
    },
    getCategoryName(cat: any) {
      if (!cat) return 'Uncategorized';
      if (typeof cat === 'object' && cat.name) return cat.name;
      const match = this.categories.find(c => c._id === cat);
      return match ? match.name : 'Unknown Category';
    },
    handleLogout() {
      localStorage.removeItem('user');
      this.$router.push('/login');
    },
    
    // Portfolio CRUD
    openPortModal(item: PortItem | null) {
      this.activeFormLang = this.languages[0];
      if (this.$refs.imagesFile) {
        (this.$refs.imagesFile as HTMLInputElement).value = '';
      }

      if (item) {
        // Edit Mode
        this.form.id = item._id;
        this.languages.forEach(lang => {
          this.form.name[lang] = item.name[lang] || '';
          this.form.link[lang] = item.link[lang] || '';
          this.form.content[lang] = item.content[lang] || '';
          this.form.technology[lang] = item.technology[lang] || '';
          this.form.api[lang] = item.api[lang] || '';
        });
        this.form.images_link = item.images || '';
        this.form.category = item.category._id || item.category || '';
        this.form.order_show = item.order_show || '';
      } else {
        // Add Mode
        this.form.id = '';
        this.languages.forEach(lang => {
          this.form.name[lang] = '';
          this.form.link[lang] = '';
          this.form.content[lang] = '';
          this.form.technology[lang] = '';
          this.form.api[lang] = '';
        });
        this.form.images_link = '';
        this.form.category = this.categories.length > 0 ? this.categories[0]._id : '';
        this.form.order_show = '';
      }
      this.showPortModal = true;
    },
    savePort() {
      const formData = new FormData();
      formData.append('id', this.form.id);
      formData.append('images_link', this.form.images_link);
      formData.append('category', this.form.category);
      formData.append('order_show', this.form.order_show);
      
      this.languages.forEach(lang => {
        formData.append(`name[${lang}]`, this.form.name[lang]);
        formData.append(`link[${lang}]`, this.form.link[lang]);
        formData.append(`content[${lang}]`, this.form.content[lang]);
        formData.append(`technology[${lang}]`, this.form.technology[lang]);
        formData.append(`api[${lang}]`, this.form.api[lang]);
      });

      const fileInput = this.$refs.imagesFile as HTMLInputElement;
      if (fileInput && fileInput.files && fileInput.files[0]) {
        formData.append('images', fileInput.files[0]);
      }

      fetch(this.baseUrl + 'update_port', {
        method: 'POST',
        body: formData
      })
      .then(res => res.json())
      .then(res => {
        if (res.success === 0) {
          alert(res.msg);
        } else {
          this.showPortModal = false;
          this.fetchPorts();
        }
      })
      .catch(err => console.error("Failed to save port", err));
    },
    deletePort(id: string) {
      if (!confirm("Are you sure you want to delete this portfolio item?")) return;
      
      const formData = new FormData();
      formData.append('delete_id', id);

      fetch(this.baseUrl + 'delete_port', {
        method: 'POST',
        body: formData
      })
      .then(res => res.json())
      .then(res => {
        if (res.success === 0) {
          alert(res.msg);
        } else {
          this.fetchPorts();
        }
      })
      .catch(err => console.error("Failed to delete port", err));
    },

    // Category CRUD
    openCategoryModal(cat: CategoryItem | null) {
      if (cat) {
        this.catForm.id = cat._id;
        this.catForm.name = cat.name;
        this.catForm.order_show = cat.order_show || '';
      } else {
        this.catForm.id = '';
        this.catForm.name = '';
        this.catForm.order_show = '';
      }
      this.showCategoryModal = true;
    },
    saveCategory() {
      fetch(this.baseUrl + 'update_category', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: this.catForm.id,
          name: this.catForm.name,
          order_show: this.catForm.order_show
        })
      })
      .then(res => res.json())
      .then(res => {
        if (res.success === 0) {
          alert(res.msg);
        } else {
          this.showCategoryModal = false;
          this.fetchCategories();
          this.fetchPorts(); // Refresh list to catch name/category changes
        }
      })
      .catch(err => console.error("Failed to save category", err));
    },
    deleteCategory(id: string) {
      if (!confirm("Delete this category? This might affect existing portfolio items.")) return;

      const formData = new FormData();
      formData.append('delete_id', id);

      fetch(this.baseUrl + 'delete_category', {
        method: 'POST',
        body: formData
      })
      .then(res => res.json())
      .then(res => {
        if (res.success === 0) {
          alert(res.msg);
        } else {
          this.fetchCategories();
          this.fetchPorts();
        }
      })
      .catch(err => console.error("Failed to delete category", err));
    }
  }
})
</script>

<style scoped>
.admin-dashboard {
  background: linear-gradient(135deg, #1f2937, #111827, #0f172a);
  min-height: 100vh;
  color: #f3f4f6;
  padding-bottom: 50px;
}

.admin-header {
  background: rgba(17, 24, 39, 0.85);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.admin-title {
  font-weight: 800;
  background: linear-gradient(90deg, #00d2ff, #00d2ff, #0066ff);
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.user-badge {
  font-size: 14px;
  color: #9ca3af;
}

.admin-tabs .tab-btn {
  background: rgba(255, 255, 255, 0.05);
  color: #d1d5db;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 24px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.admin-tabs .tab-btn.active {
  background: #0088cc;
  color: #ffffff;
  border-color: #0088cc;
  box-shadow: 0 4px 12px rgba(0, 136, 204, 0.35);
}

.dark-select {
  background-color: #1f2937;
  color: #f3f4f6;
  border: 1px solid #374151;
}

.dark-select:focus {
  background-color: #1f2937;
  color: #f3f4f6;
  border-color: #00d2ff;
  box-shadow: 0 0 6px rgba(0, 210, 255, 0.3);
}

.admin-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.admin-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.card-img-wrapper {
  height: 200px;
  overflow: hidden;
  background-color: #000;
}

.card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-info {
  flex-grow: 1;
}

.text-truncate-3 {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 6px !important;
}

.admin-table-container {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow: hidden;
}

/* Custom Modal Stylings */
.custom-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  overflow-y: auto;
}

.custom-modal-content {
  background: #1e293b;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  color: #f8fafc;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  max-height: 90vh;
}

.max-w-sm {
  max-width: 450px !important;
}

.custom-modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid #334155;
}

.custom-modal-body {
  padding: 24px;
  overflow-y: auto;
  flex-grow: 1;
}

.dark-input {
  background-color: #0f172a;
  border: 1px solid #334155;
  color: #f8fafc;
}

.dark-input:focus {
  background-color: #0f172a;
  color: #f8fafc;
  border-color: #00d2ff;
  box-shadow: 0 0 6px rgba(0, 210, 255, 0.3);
}

.dark-tabs .nav-link {
  color: #94a3b8;
  border-color: transparent;
  cursor: pointer;
}

.dark-tabs .nav-link.active {
  background-color: #0f172a;
  color: #f8fafc;
  border-color: #334155 #334155 transparent #334155;
}
</style>
